import React, {useContext, useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react';
import {PopupContainerContext} from '@src/contexts/PopupContainerContext';

export type TPopupInjectorApiRef = {
    openPopup: () => void,
    closePopup: () => void,
};

export type PopupInjectorRenderPopupProps = {
    closePopup: () => void,
    scrollableRef: React.RefObject<HTMLDivElement>,
};

export type PopupInjectorRenderButtonProps = {
    openPopup: () => void,
    closePopup: () => void,
    scrollableRef: React.RefObject<HTMLDivElement>,
};

export type PopupInjectorProps = {
    popupName: string,
    renderPopup: (props: PopupInjectorRenderPopupProps) => React.ReactNode,
    renderButton: (props: PopupInjectorRenderButtonProps) => React.ReactNode,
    stickPopupTo?: 'top' | 'bottom',
    apiRef?: React.RefObject<TPopupInjectorApiRef>,
};

export default function PopupInjector(props: PopupInjectorProps) {
    const name = props.popupName;
    const context = useContext(PopupContainerContext);
    const scrollableRef = useRef<HTMLDivElement>(null);
    const [isPopupOpen, setIsPopupOpen] = useState(false);

    useImperativeHandle(props.apiRef, () => {
        return {
            openPopup: () => setIsPopupOpen(true),
            closePopup: () => setIsPopupOpen(false),
        };
    }, []);

    useEffect(() => {
        if (!context || !name || !isPopupOpen) {
            return;
        }

        void context.openPopup(name);

        return () => void context.closePopup(name);
    }, [name, context, isPopupOpen]);

    const [popupProps, buttonProps] = useMemo(() => {
        const [openPopup, closePopup] = [
            () => setIsPopupOpen(true),
            () => setIsPopupOpen(false),
        ];

        const popupProps = {closePopup, scrollableRef};
        const buttonProps = {openPopup, closePopup, scrollableRef};

        return [popupProps, buttonProps];
    }, []);

    if (!context || !name) {
        return null;
    }

    const Portal = context.Portal;

    return <>
        <Portal
                display={isPopupOpen}
                closePopup={popupProps.closePopup}
                popupName={name}
                wrapperRef={scrollableRef}
                stickPopupTo={props.stickPopupTo}
        >
            {props.renderPopup(popupProps)}
        </Portal>

        {props.renderButton(buttonProps)}
    </>;
}
